'use client';

// Background "new version available" watcher.
//
// Pattern: version polling + Page Visibility API + non-intrusive banner.
//
// What it does:
//   1. On mount, asks /api/version for the current deploy ID and records it.
//   2. Every CHECK_INTERVAL_MS (5 min), polls again.
//   3. Also polls IMMEDIATELY whenever the tab becomes visible after being
//      hidden - covers the case where someone leaves a tab open overnight.
//   4. When the server's ID differs from what the page loaded with, shows a
//      small bottom-right toast suggesting a refresh.
//   5. Doesn't nag mid-action: if the user is actively typing in a textarea
//      or input, the banner is suppressed until they click away.
//   6. Auto-reload safety net: if a new version has been pending for over
//      AUTO_RELOAD_AFTER_MS (2h) AND the tab becomes visible, force the
//      reload - guarantees stale tabs eventually catch up without nagging.
//   7. If /api/version returns 404 for an extended period (>10 min), logs
//      a console hint that routing may have changed.
//
// Mounted once in app/layout.js - universal across user + director.

import { useEffect, useRef, useState } from 'react';
import { HV2, anyDraftDirty } from '@/components/HomeV2/lib';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;     // 5 min
// 20 min (was 2 h): when a new build is live, a stale tab force-reloads on its
// next visible+idle check within ~20 min instead of lingering for hours. This
// matters for shipping crash-fixes - a broken deploy shouldn't sit in open tabs
// half a day. Still gated on the tab being visible and the user not typing.
const AUTO_RELOAD_AFTER_MS = 20 * 60 * 1000;
const STUCK_404_AFTER_MS = 10 * 60 * 1000;   // 10 min
// Idle auto-reload: zodra een nieuwe versie klaarstaat EN de gebruiker ~2 min niets
// heeft gedaan (geen klik/toets/scroll), tab zichtbaar en niet aan het typen, ververst
// de tab zichzelf. Zo pakt een open tab de nieuwste build op tijdens een koffiepauze
// i.p.v. te blijven hangen op oude JS tot iemand handmatig 'Vernieuwen' klikt.
const IDLE_RELOAD_MS = 2 * 60 * 1000;        // 2 min zonder interactie
const IDLE_POLL_MS = 15 * 1000;              // hoe vaak we de idle-conditie checken
// Terug na een pauze: staat er een nieuwe build klaar op het moment dat iemand na
// 30 min of langer terugkomt (laptop open, tab weer zichtbaar), dan ververst de tab
// meteen, voor de eerste klik. Zonder deze regel werkte een teruggekeerde gebruiker
// tot 25 min door op oude JS, want wie werkt is nooit 2 min idle (21 sep 2026: een
// tab van 11 sep riep een op 12 sep opgeheven route aan).
const RETURN_RELOAD_MS = 30 * 60 * 1000;

// Phone breakpoint, matches the app-wide isMobile threshold (innerWidth < 768).
// This component mounts standalone in the layout (no isMobile prop reaches it),
// so it tracks the media query itself. SSR-safe: state starts false (desktop),
// the real value lands after mount.
function useIsMobileViewport() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    setIsMobile(mql.matches);
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

export default function VersionWatcher() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const isMobile = useIsMobileViewport();

  const myVersionRef = useRef(null);   // what this tab booted with
  const updateSinceRef = useRef(null); // when we first detected staleness
  const last404SinceRef = useRef(null);
  const lastActivityRef = useRef(typeof Date !== 'undefined' ? Date.now() : 0); // laatste user-interactie

  // Suppress banner while user is actively typing/editing
  function isUserBusy() {
    const el = typeof document !== 'undefined' ? document.activeElement : null;
    if (!el) return false;
    const tag = (el.tagName || '').toLowerCase();
    // Alleen een veld met inhoud houdt een reload tegen: focus die in een leeg
    // zoekveld is blijven staan blokkeerde anders elke automatische reload, voorgoed.
    if (tag === 'input' || tag === 'textarea') return String(el.value ?? '') !== '';
    if (el.isContentEditable) return true;
    return false;
  }

  // Een automatische reload wacht ook op open concepten buiten de focus: een
  // getypte maar niet opgeslagen bellog, notitie of voorwaarde (useDraftGuard)
  // verdween anders bij de idle-reload zodra de gebruiker even wegklikte.
  // Eén lezer: het bestaande draftregister uit lib.js, geen eigen telling.
  // De melding 'Nieuwe versie beschikbaar' blijft wel staan, de gebruiker kiest zelf.
  function canAutoReload() {
    return !isUserBusy() && !anyDraftDirty({ includeReloadOnly: true });
  }

  const check = async () => {
    // Voor de fetch gemeten: de eerste klik na terugkomst mag de meting niet wissen.
    const idleFor = Date.now() - lastActivityRef.current;
    try {
      const res = await fetch('/api/version', { cache: 'no-store' });
      if (res.status === 404) {
        if (!last404SinceRef.current) last404SinceRef.current = Date.now();
        if (Date.now() - last404SinceRef.current > STUCK_404_AFTER_MS) {
          console.warn('[VersionWatcher] /api/version 404 for >10 min - routing may have changed, consider reloading.');
        }
        return;
      }
      last404SinceRef.current = null;
      if (!res.ok) return;
      const data = await res.json();
      const id = data.id;
      if (!id) return;

      if (myVersionRef.current == null) {
        myVersionRef.current = id;
        return;
      }
      if (id !== myVersionRef.current) {
        if (!updateSinceRef.current) updateSinceRef.current = Date.now();
        // Auto-reload safety net: if it's been pending >2h and tab is visible,
        // pull the trigger ourselves to guarantee staleness eventually clears.
        if (
          document.visibilityState === 'visible'
          && (Date.now() - updateSinceRef.current > AUTO_RELOAD_AFTER_MS || idleFor > RETURN_RELOAD_MS)
          && canAutoReload()
        ) {
          window.location.reload();
          return;
        }
        if (!isUserBusy()) setUpdateAvailable(true);
      }
    } catch {
      /* offline / network blip - silently ignore */
    }
  };

  useEffect(() => {
    // Boot: record this tab's version
    check();
    // Verborgen tabblad: geen ronde; terug in beeld haalt onVisibility meteen op.
    const interval = setInterval(() => { if (document.visibilityState === 'visible') check(); }, CHECK_INTERVAL_MS);
    const onVisibility = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisibility);
    // Also re-check whenever the user clicks/keys after being idle - picks up
    // the banner moments after they finish typing in an input.
    const onFocusOut = () => { if (updateSinceRef.current && !isUserBusy()) setUpdateAvailable(true); };
    document.addEventListener('focusout', onFocusOut);

    // Interactie-tracking (discreet: klik/toets/scroll/touch) - stempelt de laatste
    // activiteit zodat we "idle" kunnen bepalen zonder een constante mousemove-storm.
    const bump = () => { lastActivityRef.current = Date.now(); };
    const actOpts = { passive: true, capture: true };
    for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll']) {
      window.addEventListener(ev, bump, actOpts);
    }
    // Idle auto-reload: nieuwe versie klaar + tab zichtbaar + niet aan het typen +
    // ~2 min geen interactie -> ververs vanzelf naar de nieuwste build.
    const idleTimer = setInterval(() => {
      if (!updateSinceRef.current) return;
      if (document.visibilityState !== 'visible') return;
      if (!canAutoReload()) return;
      if (Date.now() - lastActivityRef.current < IDLE_RELOAD_MS) return;
      window.location.reload();
    }, IDLE_POLL_MS);

    return () => {
      clearInterval(interval);
      clearInterval(idleTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('focusout', onFocusOut);
      for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll']) {
        window.removeEventListener(ev, bump, actOpts);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!updateAvailable || dismissed) return null;

  // Phone: docked to the bottom, stacked above TeamToastStack and Toaster so all
  // three never overlap; full width, message wraps. Desktop keeps the top-16 dock.
  const wrapStyle = isMobile
    ? {
      position: 'fixed',
      left: '12px',
      right: '12px',
      bottom: 'calc(12px + var(--pa-toaster-h, 0px) + var(--pa-teamtoast-h, 0px) + env(safe-area-inset-bottom))',
      zIndex: 9999,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '7px',
      padding: '8px 10px',
      background: HV2.bgSub,
      border: `0.5px solid ${HV2.border}`,
      borderRadius: 3,
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    }
    : {
      position: 'fixed',
      top: '16px',
      right: '16px',
      zIndex: 9999,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '7px',
      padding: '8px 10px',
      background: HV2.bgSub,
      border: `0.5px solid ${HV2.border}`,
      borderRadius: 3,
      fontFamily: 'inherit',
    };

  return (
    <div
      role="status"
      aria-live="polite"
      data-pa-overlay="version"
      style={wrapStyle}
    >
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={HV2.blue} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <polyline points="23 4 23 10 17 10" />
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
      </svg>
      <span style={{ fontSize: HV2.fsText, fontWeight: 480, color: HV2.ink2, whiteSpace: isMobile ? 'normal' : 'nowrap' }}>
        Nieuwe versie beschikbaar
      </span>
      <div style={{ display: 'flex', gap: '6px', marginLeft: '6px' }}>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '3px 8px',
            fontSize: 12,
            fontWeight: 560,
            background: HV2.blue,
            color: '#fff',
            border: 'none',
            borderRadius: '3px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            ...(isMobile ? { minHeight: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' } : null),
          }}
        >
          Vernieuwen
        </button>
        <button
          onClick={() => setDismissed(true)}
          style={{
            padding: '3px 8px',
            fontSize: 12,
            fontWeight: 480,
            background: 'transparent',
            color: HV2.muted,
            border: `0.5px solid ${HV2.border}`,
            borderRadius: '3px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            ...(isMobile ? { minHeight: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' } : null),
          }}
        >
          Later
        </button>
      </div>
    </div>
  );
}
